import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext.tsx';
import { analytics } from '../lib/analytics';
import '../styles/landing.css';

/* --- Scroll reveal hook (CSS Intersection Observer) --- */

function useScrollReveal() {
  const [observer] = useState(() => {
    if (typeof IntersectionObserver === 'undefined') return null;
    return new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' },
    );
  });

  const observe = useCallback(
    (el: HTMLElement | null) => {
      if (el && observer) observer.observe(el);
    },
    [observer],
  );

  useEffect(() => () => observer?.disconnect(), [observer]);

  return observe;
}

/* --- Data --- */

const FEATURES = [
  { icon: '\u{1F4AC}', title: 'Natural Language Generation', desc: 'Describe your diagram in plain English. Our AI generates Mermaid code instantly — flowcharts, sequences, ERDs, and more.', wide: true },
  { icon: '\u{270F}\u{FE0F}', title: 'Live Mermaid Editor', desc: 'Edit code with real-time preview, syntax validation, and auto-fix suggestions.' },
  { icon: '\u{1F3A8}', title: 'Excalidraw Whiteboard', desc: 'Switch to freeform drawing with Excalidraw. Sketch ideas without constraints.' },
  { icon: '\u{1F4C2}', title: 'Cloud Projects', desc: 'Organize diagrams into projects and workspaces. Everything syncs across devices.' },
  { icon: '\u{1F504}', title: 'Revision History', desc: 'Full version history with restore. Never lose a diagram again.' },
  { icon: '\u{1F465}', title: 'Team Workspaces', desc: 'Invite team members, share projects, and collaborate in real-time.', wide: true },
];

const STEPS = [
  { num: 1, title: 'Describe It', desc: 'Type what you need: "a login flow with OAuth and 2FA" — natural language works.' },
  { num: 2, title: 'Craft It', desc: 'AI generates the diagram. Edit the code or switch to whiteboard for tweaks.' },
  { num: 3, title: 'Done', desc: 'Export as SVG/PNG, share with your team, or keep iterating with chat.' },
];

const PLANS = [
  { name: 'Free', desc: 'For individuals getting started', price: '$0', period: 'forever', features: ['5 diagrams', '10 AI generations/month', '1 workspace', 'Community support'], featured: false },
  { name: 'Pro', desc: 'For power users and freelancers', price: '$12', period: '/month', features: ['Unlimited diagrams', '200 AI generations/month', '5 workspaces', 'Priority support', 'Export to SVG/PNG'], featured: true },
  { name: 'Team', desc: 'For teams that ship fast', price: '$29', period: '/month per seat', features: ['Everything in Pro', 'Unlimited workspaces', 'Team member management', 'Role-based access', 'Shared revision history'], featured: false },
];

const TESTIMONIALS = [
  { text: '"Sketchflow replaced three tools for us. The AI generation is scarily good — it nails sequence diagrams on the first try."', name: 'Sarah K.', role: 'Staff Engineer', initial: 'S' },
  { text: '"The Excalidraw integration is chef\'s kiss. I draft in whiteboard mode, then refine with Mermaid code."', name: 'Marcus T.', role: 'Product Designer', initial: 'M' },
  { text: '"We onboarded the whole team in 10 minutes. The workspace sharing makes architecture reviews painless."', name: 'Aisha R.', role: 'Engineering Manager', initial: 'A' },
];

const FAQ_ITEMS = [
  { q: 'What diagram types are supported?', a: 'Flowcharts, sequence diagrams, class diagrams, ER diagrams, state diagrams, Gantt charts, pie charts, and more — anything Mermaid supports, plus freeform Excalidraw drawings.' },
  { q: 'How does AI generation work?', a: 'Describe what you need in plain language. Our AI (powered by Claude) generates Mermaid code that renders instantly. You can refine with follow-up prompts in the chat sidebar.' },
  { q: 'Can I use it offline?', a: 'The editor works locally for drawing and editing. AI generation and cloud sync require an internet connection.' },
  { q: 'Is my data secure?', a: 'All data is encrypted in transit and at rest. We never train on your diagrams. Workspace isolation ensures team data stays private.' },
  { q: 'What payment methods do you accept?', a: 'We accept PayPal for all subscription plans. You can cancel anytime from your account settings.' },
];

/* --- Component --- */

export function LandingPage() {
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [navScrolled, setNavScrolled] = useState(false);
  const observe = useScrollReveal();
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  // Redirect authenticated users to app
  useEffect(() => {
    if (!isLoading && isAuthenticated) navigate('/app', { replace: true });
  }, [isAuthenticated, isLoading, navigate]);

  // Navbar scroll effect
  useEffect(() => {
    const onScroll = () => setNavScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return (
    <div className="landing">
      {/* Navbar */}
      <nav className={`landing-nav ${navScrolled ? 'landing-nav--scrolled' : ''}`}>
        <a href="#" className="landing-nav__brand">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" className="landing-nav__logo">
            <path d="M3 4l9 16 9-16" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
            <path d="M7 4h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" opacity="0.5" />
          </svg>
          <span className="landing-nav__name">Sketchflow</span>
        </a>
        <div className="landing-nav__links">
          <a href="#features" className="landing-nav__link">Features</a>
          <a href="#how-it-works" className="landing-nav__link">How it works</a>
          <a href="#pricing" className="landing-nav__link">Pricing</a>
          <a href="#faq" className="landing-nav__link">FAQ</a>
        </div>
        <div className="landing-nav__actions">
          <Link to="/login" className="landing-nav__signin">Sign in</Link>
          <Link to="/register" className="landing-btn landing-btn--primary landing-btn--sm" onClick={() => analytics.ctaClicked('nav_get_started')}>
            Get started free
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <section className="landing-hero">
        <div className="landing-hero__glow" />
        <div className="landing-hero__badge">&#10022; AI-Powered Diagram Studio</div>
        <h1 className="landing-hero__title">
          Describe it. <span className="landing-hero__gradient">Watch it</span> come alive.
        </h1>
        <p className="landing-hero__subtitle">
          Generate beautiful diagrams from natural language. Edit with code or whiteboard.
          Collaborate with your team — all in one place.
        </p>
        <div className="landing-hero__actions">
          <Link to="/register" className="landing-btn landing-btn--primary landing-btn--lg" onClick={() => analytics.ctaClicked('hero_start_free')}>
            Start creating for free
          </Link>
          <a href="#how-it-works" className="landing-btn landing-btn--ghost landing-btn--lg" onClick={() => analytics.featureClicked('hero_see_how')}>
            See how it works
          </a>
        </div>
      </section>

      {/* Features Bento */}
      <section id="features" className="landing-section landing-section--center reveal" ref={observe}>
        <div className="landing-section__label">Features</div>
        <h2 className="landing-section__title">Everything you need to ship diagrams fast</h2>
        <p className="landing-section__desc">
          From AI generation to team collaboration — Sketchflow is the modern diagram studio.
        </p>
        <div className="landing-bento">
          {FEATURES.map((f) => (
            <div key={f.title} className={`landing-bento__card reveal reveal-stagger ${f.wide ? 'landing-bento__card--wide' : ''}`} ref={observe}>
              <div className="landing-bento__icon">{f.icon}</div>
              <div className="landing-bento__title">{f.title}</div>
              <div className="landing-bento__desc">{f.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how-it-works" className="landing-section landing-section--center reveal" ref={observe}>
        <div className="landing-section__label">How it works</div>
        <h2 className="landing-section__title">Three steps. Zero friction.</h2>
        <p className="landing-section__desc">
          No learning curve. Just describe what you need and let the AI handle the rest.
        </p>
        <div className="landing-steps">
          {STEPS.map((s) => (
            <div key={s.num} className="landing-step reveal reveal-stagger" ref={observe}>
              <div className="landing-step__number">{s.num}</div>
              <div className="landing-step__title">{s.title}</div>
              <div className="landing-step__desc">{s.desc}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="pricing" className="landing-section landing-section--center reveal" ref={observe}>
        <div className="landing-section__label">Pricing</div>
        <h2 className="landing-section__title">Simple, transparent pricing</h2>
        <p className="landing-section__desc">Start free. Upgrade when you need more power. Cancel anytime.</p>
        <div className="landing-pricing">
          {PLANS.map((plan) => (
            <div key={plan.name} className={`landing-price-card reveal reveal-stagger ${plan.featured ? 'landing-price-card--featured' : ''}`} ref={observe}>
              {plan.featured && <div className="landing-price-card__badge">Most popular</div>}
              <div className="landing-price-card__name">{plan.name}</div>
              <div className="landing-price-card__desc">{plan.desc}</div>
              <div className="landing-price-card__amount">{plan.price}</div>
              <div className="landing-price-card__period">{plan.period}</div>
              <ul className="landing-price-card__features">
                {plan.features.map((f) => (
                  <li key={f} className="landing-price-card__feature">{f}</li>
                ))}
              </ul>
              <Link
                to="/register"
                className={`landing-btn ${plan.featured ? 'landing-btn--primary' : 'landing-btn--ghost'}`}
                style={{ width: '100%', justifyContent: 'center' }}
                onClick={() => { analytics.pricingViewed(); analytics.planSelected(plan.name.toLowerCase()); }}
              >
                {plan.price === '$0' ? 'Get started' : 'Start free trial'}
              </Link>
            </div>
          ))}
        </div>
      </section>

      {/* Testimonials */}
      <section className="landing-section landing-section--center reveal" ref={observe}>
        <div className="landing-section__label">Testimonials</div>
        <h2 className="landing-section__title">Loved by teams worldwide</h2>
        <div className="landing-testimonials">
          {TESTIMONIALS.map((t) => (
            <div key={t.name} className="landing-testimonial reveal reveal-stagger" ref={observe}>
              <div className="landing-testimonial__text">{t.text}</div>
              <div className="landing-testimonial__author">
                <div className="landing-testimonial__avatar">{t.initial}</div>
                <div>
                  <div className="landing-testimonial__name">{t.name}</div>
                  <div className="landing-testimonial__role">{t.role}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section id="faq" className="landing-section landing-section--center reveal" ref={observe}>
        <div className="landing-section__label">FAQ</div>
        <h2 className="landing-section__title">Frequently asked questions</h2>
        <div className="landing-faq">
          {FAQ_ITEMS.map((item, i) => (
            <div key={i} className={`landing-faq__item reveal reveal-stagger ${openFaq === i ? 'landing-faq__item--open' : ''}`} ref={observe}>
              <button className="landing-faq__question" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                {item.q}
                <span className="landing-faq__arrow">{'\u25BC'}</span>
              </button>
              <div className="landing-faq__answer">{item.a}</div>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="landing-cta reveal" ref={observe}>
        <div className="landing-cta__glow" />
        <h2 className="landing-cta__title">
          Ready to <span className="landing-hero__gradient">craft</span> your diagrams?
        </h2>
        <p className="landing-cta__desc">
          Join thousands of developers and designers creating diagrams faster with AI.
        </p>
        <Link to="/register" className="landing-btn landing-btn--primary landing-btn--lg" onClick={() => analytics.ctaClicked('bottom_cta')}>
          Start creating for free
        </Link>
      </section>

      {/* Footer */}
      <footer className="landing-footer">
        <div className="landing-footer__top">
          <div className="landing-footer__brand">
            <div className="landing-footer__brand-name">Sketchflow</div>
            <div className="landing-footer__brand-desc">AI-powered diagram studio. Describe it. Watch it come alive.</div>
          </div>
          <div className="landing-footer__cols">
            <div>
              <div className="landing-footer__col-title">Product</div>
              <ul className="landing-footer__col-links">
                <li><a href="#features" className="landing-footer__col-link">Features</a></li>
                <li><a href="#pricing" className="landing-footer__col-link">Pricing</a></li>
                <li><a href="#faq" className="landing-footer__col-link">FAQ</a></li>
              </ul>
            </div>
            <div>
              <div className="landing-footer__col-title">Company</div>
              <ul className="landing-footer__col-links">
                <li><a href="#" className="landing-footer__col-link">About</a></li>
                <li><a href="#" className="landing-footer__col-link">Blog</a></li>
                <li><a href="#" className="landing-footer__col-link">Contact</a></li>
              </ul>
            </div>
            <div>
              <div className="landing-footer__col-title">Legal</div>
              <ul className="landing-footer__col-links">
                <li><a href="#" className="landing-footer__col-link">Privacy</a></li>
                <li><a href="#" className="landing-footer__col-link">Terms</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="landing-footer__bottom">
          &copy; {new Date().getFullYear()} Sketchflow. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
